import { Connection, ConnectionDto, ConnectionName, ConnectionStatus, ConnectionType } from "@arelia-types/connection";
import getAccountConnectionIcon from "@arelia-utils/helpers/getAccountConnectionIcon";
import { sortBy } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import ConnectionsContext from "./ConnectionsContext";
import { ConnectionTypeMap } from "./constants";

export function useConnectionsDto(connectionsResponse: Connection[] | undefined): ConnectionDto[] {
  const contSupportedConnections: any[] = [];

  return useMemo(() => sortBy(connectionsResponse, [x => x.name !== ConnectionName.arelia, x => x.name])
    .map(cr => {
      let icon = getAccountConnectionIcon(cr.name);
      let status: ConnectionStatus;
      const type = ConnectionTypeMap[cr.name];
      if (contSupportedConnections.includes(cr.name)) {
        status = ConnectionStatus.notSupported;
      } else {
        status = cr.connected ? (cr.unauthorized ? ConnectionStatus.notAuthorized : ConnectionStatus.connected) : ConnectionStatus.notConnected;
      }

      return {
        ...cr,
        icon,
        status,
        type
      };
    }), [connectionsResponse]
  );
}

export function useViewConnections(
  activeConnections: ConnectionName[],
  connections: ConnectionDto[],
  filter: ConnectionType | null
) {
  return useMemo(() => connections
    .filter(c => c.connected || activeConnections.some(ac => ac === c.name))
    .filter(c => !filter || c.type === null || c.type.includes(filter)),
  [activeConnections, connections, filter]);
}

export function useActiveConnections(): [ConnectionName[], (name: ConnectionName[]) => void] {
  const [activeConnections, setActiveConnections] = useState<ConnectionName[]>(() => {
    const saved = localStorage.getItem('activeConnections');
    const parsed = saved && JSON.parse(saved) as ConnectionName[];
    return parsed || [ConnectionName.arelia];
  });

  useEffect(() => {
    localStorage.setItem('activeConnections', JSON.stringify(activeConnections));
  }, [activeConnections]);

  return [activeConnections, setActiveConnections];
}

export function useInactiveConnections() {
  const { connectionsDto, activeConnections } = useContext(ConnectionsContext);

  return useMemo(() => connectionsDto.filter((c) => !activeConnections.includes(c.name)), [connectionsDto, activeConnections]);
}
